import { useState } from 'react';

import { formatCurrency } from '@/utils';

import type { LifeIllustration } from './proposalFlow';

/** Columns in the order an insurer prints them; a column nobody filled is not drawn. */
const COLUMNS: { key: string; label: string }[] = [
  { key: 'annual_premium', label: 'Premium' },
  { key: 'death_benefit', label: 'Death benefit' },
  { key: 'total_death_benefit', label: 'Total death benefit' },
  { key: 'survival_benefit', label: 'Survival benefit' },
  { key: 'maturity_benefit', label: 'Maturity benefit' },
  { key: 'maturity_additions', label: 'Additions' },
  { key: 'guaranteed_surrender_value', label: 'Guaranteed surrender' },
  { key: 'surrender_value', label: 'Surrender value' },
  { key: 'special_surrender_value', label: 'Special surrender' },
];

/**
 * Board #587: the year-by-year illustration a life insurer returned for an option
 * (`Quote.additional_data["illustration"]`, served allowlisted by the quote
 * serializer). Collapsed by default; draws nothing when there are no rows.
 */
export function LifeIllustrationTable({ illustration }: { illustration?: LifeIllustration | null }) {
  const [open, setOpen] = useState(false);
  const rows = illustration?.rows ?? [];
  if (!illustration || rows.length === 0) return null;

  const columns = COLUMNS.filter((c) => rows.some((r) => r[c.key] !== undefined && r[c.key] !== null));
  return (
    <div className="mt-3" data-testid="life-illustration">
      <button
        type="button"
        className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide illustration' : `Show illustration (${rows.length} years)`}
      </button>
      {open && (
        <div className="mt-2 overflow-x-auto">
          <table className="min-w-full text-left text-xs text-gray-700 dark:text-gray-200">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="py-1 pr-3">Year</th>
                <th className="py-1 pr-3">Age</th>
                {columns.map((c) => (
                  <th key={c.key} className="py-1 pr-3">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.year} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-1 pr-3">{row.year}</td>
                  <td className="py-1 pr-3">{row.age ?? '—'}</td>
                  {columns.map((c) => (
                    <td key={c.key} className="py-1 pr-3">
                      {row[c.key] === undefined ? '—' : formatCurrency(row[c.key] as string)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
